import { useCallback } from 'react'
import { useInvalidate } from '@refinedev/core'
import { useQueryClient } from '@tanstack/react-query'
import { ordersGetFulfillmentOrderCountsQueryKey } from '@repo/sdk'

/**
 * Refreshes every fulfillment-order view after a mutation (ship / change tracking / deliver).
 *
 * refine keys its queries as ['data', <provider>, 'order-list', ...], so a plain
 * `invalidateQueries({ queryKey: ['order-list'] })` never matched the list or the detail —
 * pages kept showing stale status after shipping. Use refine's own invalidator for those,
 * and tanstack for the custom queries (buyer new-order groups, live tracking status).
 *
 * `invalidates: ['detail']` builds the key as `...action('one').id(id || '')`, so it only
 * matches when the fulfillment order id is passed along. Without an id we widen to
 * `resourceAll`, which covers the list plus every cached detail — use that for mutations whose
 * effect spans more than one fulfillment order (delivery covers the whole combined parcel).
 */
export function useInvalidateFulfillmentOrders() {
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()

  return useCallback(
    async (fulfillmentOrderId?: string) => {
      await Promise.all([
        invalidate(
          fulfillmentOrderId
            ? {
                resource: 'order-list',
                invalidates: ['list', 'detail'],
                id: fulfillmentOrderId,
              }
            : { resource: 'order-list', invalidates: ['resourceAll'] },
        ),
        queryClient.invalidateQueries({ queryKey: ['dealer-buyer-new-orders'] }),
        queryClient.invalidateQueries({
          queryKey: ordersGetFulfillmentOrderCountsQueryKey({ query: { role: 'DEALER' } }),
        }),
        // Same rule as above: an id narrows to that order's tracking card, no id refreshes all.
        queryClient.invalidateQueries({
          queryKey: fulfillmentOrderId
            ? ['fo-tracking-status', fulfillmentOrderId]
            : ['fo-tracking-status'],
        }),
      ])
    },
    [invalidate, queryClient],
  )
}
